import React, { useEffect, useRef, useState } from "react";

/**
 * Mock: Devices-into-frame entrance animation (WEB ONLY)
 * — Monitor slides in from off-screen LEFT into its position inside the grey box
 * — Phone slides in from off-screen RIGHT into its position inside the grey box
 * — Animation triggers ONCE on down-scroll (when the frame enters the viewport)
 * — Devices STAY IN PLACE on subsequent scroll (no reverse on up-scroll)
 */
export default function ScrollDemoMock2() {
  const frameRef = useRef(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          // Play the entrance every time the frame re-enters the viewport.
          // When it fully leaves, reset so the next entry re-triggers.
          if (e.isIntersecting) {
            setEntered(true);
          } else {
            setEntered(false);
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const ease = "cubic-bezier(0.22, 1, 0.36, 1)";
  const duration = 1800; // ms — slower, very smooth entrance

  return (
    <main
      data-testid="devices-entrance-mock"
      className="min-h-screen bg-black text-white"
      style={{ fontFamily: `"SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif` }}
    >
      {/* Spacer so user has to scroll to trigger */}
      <section className="max-w-4xl mx-auto px-6 pt-24 pb-16 text-center">
        <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-semibold">Devices entrance · mock</div>
        <h1
          className="mt-4 text-4xl md:text-6xl font-semibold tracking-tight"
          style={{ letterSpacing: "-0.02em", fontFamily: `"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif` }}
        >
          Scroll down.<br />Devices fly in.
        </h1>
        <p className="mt-6 text-[15px] text-white/60 max-w-lg mx-auto leading-relaxed">
          Monitor enters from the left, phone from the right — quick, confident, Apple-style. Once seated they stay put on further scrolls.
        </p>
        <div className="mt-10 text-[12px] tracking-widest uppercase text-white/50 animate-bounce">↓ scroll</div>
      </section>

      {/* Empty scroll runway */}
      <div style={{ height: "60vh" }} />

      {/* Grey device frame */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div
          ref={frameRef}
          data-testid="device-frame"
          className="rounded-3xl overflow-hidden px-6 py-24"
          style={{ background: "#1D1D1F" }}
        >
          <div className="w-full max-w-3xl mx-auto flex items-center justify-center gap-10 lg:gap-14">
            {/* Monitor — slides in from left */}
            <img
              src="/images/tech-monitor.png"
              alt="PropHero community portal dashboard"
              data-testid="monitor-img"
              className="block h-auto w-[62%]"
              style={{
                transform: entered ? "translateX(0)" : "translateX(-120vw)",
                opacity: entered ? 1 : 0,
                // Only animate on ENTRY (entered = true). When entered goes false
                // the frame is already out of view, so a snap-reset is invisible
                // and avoids a rewind animation.
                transition: entered
                  ? `transform ${duration}ms ${ease}, opacity ${duration * 0.6}ms ease`
                  : "none",
                willChange: "transform, opacity",
              }}
            />
            {/* Phone — slides in from right (same time as monitor) */}
            <img
              src="/images/tech-phone.png"
              alt="PropHero mobile app"
              data-testid="phone-img"
              className="block h-auto self-center w-[15%]"
              style={{
                transform: entered ? "translateX(0)" : "translateX(120vw)",
                opacity: entered ? 1 : 0,
                transition: entered
                  ? `transform ${duration}ms ${ease}, opacity ${duration * 0.6}ms ease`
                  : "none",
                willChange: "transform, opacity",
              }}
            />
          </div>
        </div>
      </section>

      {/* Outro spacer */}
      <section className="max-w-4xl mx-auto px-6 pb-32 text-center">
        <p className="text-white/50 text-[13px]">Scroll back up — devices stay in place.</p>
      </section>
    </main>
  );
}
